import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import CloseIcon from '../../assets/svg-components/close_icon.svg';
import { CARD_ANIMATION_ENDED } from '../../store/constants';
import { StoreContext } from '../../store/store';
import { cn } from '../../utils/classname';
import './flip-card.scss';
import { animateHero, flipWithoutAnimation } from './utils/utils';

type FlipCardProps = {
  cardId: string;
  frontContent: React.ReactElement;
  backContent: React.ReactElement;
};

export const FlipCard: React.FC<FlipCardProps> = ({ cardId, frontContent, backContent }) => {
  const cardRef = useRef(null);
  const cardFrontRef = useRef(null);
  const cardBackRef = useRef(null);
  const animationContainerRef = useRef(null);

  const { state, dispatch } = useContext(StoreContext);
  const [flipped, setFlipped] = useState(false);

  const triggerFlip = useCallback(() => {
    animateHero({
      cardFrontRef,
      cardBackRef,
      cardRef,
      animationContainerRef,
      flipped,
      onComplete: () => {
        const nextFlipState = !flipped;
        setFlipped(nextFlipState);
        dispatch({
          type: CARD_ANIMATION_ENDED,
          payload: { id: cardId, isFlipped: nextFlipState, triggerAnimation: false }
        });
      }
    });
  }, [flipped, cardFrontRef, cardBackRef, cardRef, animationContainerRef, dispatch, cardId]);

  React.useEffect(() => {
    if (state.activeCard.id !== cardId) {
      flipWithoutAnimation({
        cardFrontRef,
        cardBackRef,
        flipped: true,
        onComplete: () => setFlipped(false)
      });

      return;
    }

    if (state.activeCard.triggerAnimation && !flipped) {
      triggerFlip();
    }

    if (state.activeCard.isFlipped && !flipped) {
      flipWithoutAnimation({
        cardFrontRef,
        cardBackRef,
        flipped,
        onComplete: () => setFlipped(!flipped)
      });
    }
  }, [state.activeCard, flipped, setFlipped, cardId, triggerFlip]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (flipped && cardRef.current && !cardRef.current.contains(event.target)) {
        triggerFlip();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (flipped && event.key === 'Escape') {
        triggerFlip();
      }
    };

    if (flipped) {
      window.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [flipped, triggerFlip]);

  return (
    <div className="bd-flip-card" ref={cardRef}>
      <div className="bd-flip-card__front" ref={cardFrontRef} onClick={triggerFlip}>
        {!flipped && frontContent}
      </div>
      <div className={cn('bd-flip-card__back', flipped && 'bd-flip-card__back--animated')} ref={cardBackRef}>
        <CloseIcon className="bd-flip-card__close" onClick={triggerFlip} />

        <div className="inner">{flipped && backContent}</div>
      </div>
      <div className="bd-flip-card__clone-wrapper" ref={animationContainerRef}></div>
    </div>
  );
};
